import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDownload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  type FailedDocument,
  type IngestionDashboard,
  useAdminMutation,
  useAdminQuery,
} from "../../api/admin";
import { api } from "../../api/client";
import { formatDateTime, formatNumber } from "../../lib/format";
import { StatusBadge } from "../upload/upload-page";

export function IngestionPage() {
  const { t } = useTranslation();
  const dash = useAdminQuery<IngestionDashboard>("ingestion", "/api/admin/ingestion", 10_000);
  const failed = useAdminQuery<{ documents: FailedDocument[]; deadLetters: unknown[] }>(
    "failed",
    "/api/admin/ingestion/failed",
    15_000,
  );
  const retry = useAdminMutation(
    (id: number) => api(`/api/admin/ingestion/${id}/retry`, { method: "POST" }),
    ["ingestion", "failed"],
  );

  if (dash.isError) return <Alert color="red">{(dash.error as Error).message}</Alert>;
  const d = dash.data;
  if (!d) return <Text c="dimmed">{t("common.loading")}</Text>;
  const queued = d.queues.filter((q) => q.state === "created" || q.state === "retry");
  const active = d.queues.filter((q) => q.state === "active");
  const failedJobs = d.queues.filter((q) => q.state === "failed");

  const card = (title: string, value: React.ReactNode, hint?: string) => (
    <Paper withBorder p="sm">
      <Text size="xs" c="dimmed">
        {title}
      </Text>
      <Text size="xl" fw={600}>
        {value}
      </Text>
      {hint && (
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      )}
    </Paper>
  );

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 2, md: 4 }}>
        {card(
          t("admin.ingestion.ready"),
          d.documents.find((s) => s.status === "ready")?.count ?? 0,
          t("admin.ingestion.readyHint", { hour: d.ready.lastHour, day: d.ready.lastDay }),
        )}
        {card(
          t("admin.ingestion.inProgress"),
          d.documents
            .filter((s) => !["ready", "failed", "superseded", "deleted"].includes(s.status))
            .reduce((a, s) => a + s.count, 0),
          `${t("admin.ingestion.queued")}: ${queued.reduce((a, q) => a + q.count, 0)} · ${t("admin.ingestion.active")}: ${active.reduce((a, q) => a + q.count, 0)}`,
        )}
        {card(
          t("admin.ingestion.failed"),
          d.documents.find((s) => s.status === "failed")?.count ?? 0,
          failedJobs.length
            ? `${t("admin.ingestion.failedJobs")}: ${failedJobs.reduce((a, q) => a + q.count, 0)}`
            : undefined,
        )}
        {card(t("admin.ingestion.vectors"), formatNumber(d.embedding.vectors), d.embedding.model)}
      </SimpleGrid>

      <Group align="flex-start" gap="md" wrap="wrap">
        <Paper withBorder p="sm" style={{ flex: 1, minWidth: 280 }}>
          <Text fw={600} size="sm" mb={4}>
            {t("admin.ingestion.byStatus")}
          </Text>
          <Group gap={6} wrap="wrap">
            {d.documents.map((s) => (
              <Group key={s.status} gap={4}>
                <StatusBadge status={s.status} />
                <Text size="sm">{s.count}</Text>
              </Group>
            ))}
          </Group>
          <Text fw={600} size="sm" mt="sm" mb={4}>
            {t("admin.ingestion.inbox")}
          </Text>
          <Text size="xs" c="dimmed">
            {d.inboxDir} ({d.inboxMode})
          </Text>
          <Group gap={6} wrap="wrap">
            {d.inbox.length === 0 && (
              <Text size="sm" c="dimmed">
                —
              </Text>
            )}
            {d.inbox.map((s) => (
              <Badge
                key={s.status}
                variant="light"
                color={s.status === "unmapped" || s.status === "failed" ? "orange" : "gray"}
              >
                {t(`admin.inbox.status.${s.status}`, s.status)}: {s.count}
              </Badge>
            ))}
          </Group>
        </Paper>
        <Paper withBorder p="sm" style={{ flex: 1, minWidth: 280 }}>
          <Text fw={600} size="sm" mb={4}>
            {t("admin.ingestion.stages")}
          </Text>
          <Table verticalSpacing={2} withRowBorders={false}>
            <Table.Tbody>
              {d.stages24h.map((s) => (
                <Table.Tr key={s.stage}>
                  <Table.Td>{s.stage}</Table.Td>
                  <Table.Td ta="right">{s.count}</Table.Td>
                  <Table.Td ta="right" c="dimmed">
                    {s.avgMs !== null ? `${(s.avgMs / 1000).toFixed(1)} s` : ""}
                  </Table.Td>
                </Table.Tr>
              ))}
              {d.stages24h.length === 0 && (
                <Table.Tr>
                  <Table.Td c="dimmed">—</Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Paper>
      </Group>

      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fw={600}>{t("admin.ingestion.failedDocs")}</Text>
          <Button
            component="a"
            href="/api/admin/diagnostics"
            download
            variant="default"
            size="xs"
            leftSection={<IconDownload size={14} />}
            title={t("admin.ingestion.diagnosticsHint")}
          >
            {t("admin.ingestion.diagnostics")}
          </Button>
        </Group>
        {(failed.data?.documents ?? []).length === 0 && (
          <Text size="sm" c="dimmed">
            {t("admin.ingestion.noFailed")}
          </Text>
        )}
        {(failed.data?.documents ?? []).length > 0 && (
          <Table.ScrollContainer minWidth={640}>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("document.file")}</Table.Th>
                  <Table.Th>{t("common.error")}</Table.Th>
                  <Table.Th>{t("upload.source")}</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {failed.data?.documents.map((doc) => (
                  <Table.Tr key={doc.id}>
                    <Table.Td>
                      <Anchor component={Link} to={`/documents/${doc.id}`} size="sm">
                        #{doc.id} {doc.originalName}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="red" lineClamp={3}>
                        {doc.error}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        {doc.sourceKind} {doc.sourceRef ?? ""}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="compact-xs"
                        loading={retry.isPending && retry.variables === doc.id}
                        onClick={() =>
                          retry
                            .mutateAsync(doc.id)
                            .then(() =>
                              notifications.show({
                                message: t("admin.ingestion.retried"),
                                color: "green",
                              }),
                            )
                            .catch((e: Error) =>
                              notifications.show({ message: e.message, color: "red" }),
                            )
                        }
                      >
                        {t("common.retry")}
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>

      <Stack gap="xs">
        <Text fw={600}>{t("admin.ingestion.recentErrors")}</Text>
        {d.recentErrors.length === 0 && (
          <Text size="sm" c="dimmed">
            —
          </Text>
        )}
        {d.recentErrors.map((e) => (
          <Text key={e.id} size="xs">
            <Text span c="dimmed">
              {formatDateTime(e.createdAt)}
            </Text>{" "}
            <Anchor component={Link} to={`/documents/${e.documentId}`}>
              #{e.documentId}
            </Anchor>{" "}
            {e.stage}: {e.message}
          </Text>
        ))}
      </Stack>
    </Stack>
  );
}
